import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useShop } from '../App';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const COLORS = ['#6366f1', '#f43f5e', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444', '#06b6d4', '#84cc16', '#e11d48'];

const METRICS = [
  { key: 'payments', label: 'Payments' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'repairs', label: 'Repairs' },
  { key: 'custom_jobs', label: 'Custom Jobs' },
  { key: 'appraisals', label: 'Appraisals' },
];

const GRANULARITIES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'annual', label: 'Annual' },
];

// Format a value according to whether it is money or a count.
const fmt = (v, money = true) =>
  money
    ? '$' + (parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : (parseInt(v) || 0).toLocaleString('en-US');

// Format date as YYYY-MM-DD using LOCAL time. (toISOString() is UTC, which in
// negative-offset timezones like America/New_York rolls the date forward a
// day during the evening hours.)
const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Pretty bucket label for chart axis / table, by granularity.
const formatBucket = (bucket, granularity) => {
  const d = new Date(bucket + 'T00:00:00Z');
  if (granularity === 'annual') return String(d.getUTCFullYear());
  if (granularity === 'monthly') return d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }) + ' ' + d.getUTCFullYear();
  if (granularity === 'weekly') return 'Wk of ' + d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
};

// KPI Card component
const KPICard = ({ title, value, sub, color = 'indigo' }) => {
  const bgColors = {
    indigo: 'bg-indigo-50 border-indigo-100',
    emerald: 'bg-emerald-50 border-emerald-100',
    amber: 'bg-amber-50 border-amber-100',
    rose: 'bg-rose-50 border-rose-100',
    sky: 'bg-sky-50 border-sky-100',
  };
  const textColors = {
    indigo: 'text-indigo-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    sky: 'text-sky-700',
  };
  return (
    <div className={`${bgColors[color]} border rounded-xl p-3 shadow-sm`}>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className={`text-xl font-bold ${textColors[color]} mt-0.5`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5 truncate" title={sub}>{sub}</p>}
    </div>
  );
};

export default function Performance() {
  const { shops } = useShop();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [shopId, setShopId] = useState('');
  const [quickRange, setQuickRange] = useState('');
  const [granularity, setGranularity] = useState('monthly');
  const [metric, setMetric] = useState('payments');
  const [by, setBy] = useState('shop');
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // One query result drives everything on the page
  const [result, setResult] = useState(null);

  const today = new Date();

  // Quick date range handlers
  // This Week: Sunday of this week → Today
  const setThisWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    setDateFrom(formatDate(sundayThisWeek));
    setDateTo(formatDate(today));
  };

  // Last Week: Sunday of last week → Sunday of this week
  const setLastWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayLastWeek = new Date(sundayThisWeek);
    sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
    setDateFrom(formatDate(sundayLastWeek));
    setDateTo(formatDate(sundayThisWeek));
  };

  // This Month: 1st of this month → Today
  const setThisMonth = () => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(formatDate(monthStart));
    setDateTo(formatDate(today));
  };

  // Last Month: 1st of last month → 1st of this month
  const setLastMonth = () => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setDateFrom(formatDate(lastMonthStart));
    setDateTo(formatDate(thisMonthStart));
  };

  // This Quarter: 1st of this quarter → Today
  const setThisQuarter = () => {
    const quarter = Math.floor(today.getMonth() / 3);
    const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
    setDateFrom(formatDate(quarterStart));
    setDateTo(formatDate(today));
  };

  // Last Quarter: 1st of last quarter → 1st of this quarter
  const setLastQuarter = () => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
    const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
    setDateFrom(formatDate(lastQuarterStart));
    setDateTo(formatDate(thisQuarterStart));
  };

  // This Year: Jan 1 this year → Today
  const setThisYear = () => {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(yearStart));
    setDateTo(formatDate(today));
  };

  // Last Year: Jan 1 last year → Jan 1 this year
  const setLastYear = () => {
    const lastYear = today.getFullYear() - 1;
    const lastYearStart = new Date(lastYear, 0, 1);
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(lastYearStart));
    setDateTo(formatDate(thisYearStart));
  };

  // All Time: 2000-01-01 → Today
  const setAllTime = () => {
    setDateFrom('2000-01-01');
    setDateTo(formatDate(today));
  };

  // Quick range dropdown handler
  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    setQuickRange(range);
    const rangeHandlers = {
      'this-week': setThisWeek,
      'last-week': setLastWeek,
      'this-month': setThisMonth,
      'last-month': setLastMonth,
      'this-quarter': setThisQuarter,
      'last-quarter': setLastQuarter,
      'this-year': setThisYear,
      'last-year': setLastYear,
      'all-time': setAllTime,
    };
    if (rangeHandlers[range]) {
      rangeHandlers[range]();
    } else {
      setDateFrom('');
      setDateTo('');
    }
  };

  // The single query: metric × by × granularity × date range (× optional shop)
  const fetchMetrics = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setHasSearched(true);
    setLoading(true);
    setError('');

    try {
      const p = new URLSearchParams();
      p.set('metric', metric);
      p.set('by', by);
      p.set('granularity', granularity);
      p.set('date_from', dateFrom);
      p.set('date_to', dateTo);
      if (shopId) p.set('shop_id', shopId);

      const res = await fetch(`${API_URL}/performance/metrics?${p}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setResult(await res.json());
    } catch (err) {
      console.error('Error fetching performance metrics:', err);
      setError('Failed to load performance data. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, shopId, metric, by, granularity]);

  // Auto-fetch whenever any selector changes (selectors that require a range)
  useEffect(() => {
    if (dateFrom && dateTo) fetchMetrics();
  }, [fetchMetrics]);

  // CSV export of the ranked table
  const exportCSV = () => {
    if (!result || !result.rows.length) return;
    const metricLabel = result.metricLabel;
    const headers = [by === 'shop' ? 'Shop' : 'User', metricLabel, result.contextLabel];
    const rows = result.rows.map((r) => [r.name, r.value, r.context]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-${metric}-${by}-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Reset filters
  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setShopId('');
    setQuickRange('');
    setGranularity('monthly');
    setMetric('payments');
    setBy('shop');
    setHasSearched(false);
    setResult(null);
  };

  const kpiValue = (v) => fmt(v, result?.money);
  const contextValue = (v) => fmt(v, result?.contextMoney);

  // Percentage of total for the ranked table
  const pctOfTotal = (v) => {
    if (!result || !result.kpis.total) return '0.0%';
    return ((v / result.kpis.total) * 100).toFixed(1) + '%';
  };

  const groupName = by === 'shop' ? 'Shop' : 'User';

  return (
    <div className="px-4 py-1 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          {hasSearched && (
            <button onClick={handleReset} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
              Reset Filters
            </button>
          )}
        </div>

        {/* Filter Bar */}
        <div className="bg-white shadow-sm rounded-xl p-4 mb-6 border">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {METRICS.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">By</label>
              <select
                value={by}
                onChange={(e) => setBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="shop">Shop</option>
                <option value="user">User</option>
              </select>
            </div>
            {by === 'shop' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Shop</label>
                <select
                  value={shopId}
                  onChange={(e) => setShopId(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Shops</option>
                  {shops.map((s) => (
                    <option key={s.id} value={s.shop_id}>{s.shop_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Quick Range</label>
              <select
                value={quickRange}
                onChange={handleQuickRangeChange}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Range</option>
                <option value="this-week">This Week</option>
                <option value="last-week">Last Week</option>
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="this-quarter">This Quarter</option>
                <option value="last-quarter">Last Quarter</option>
                <option value="this-year">This Year</option>
                <option value="last-year">Last Year</option>
                <option value="all-time">All Time</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Granularity</label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {GRANULARITIES.map((g) => (
                  <option key={g.key} value={g.key}>{g.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {!hasSearched ? (
          <div className="bg-white shadow-sm rounded-xl p-12 text-center border">
            <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Pick a metric and a date range</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose what to measure (payments, transactions, repairs, custom jobs, appraisals), group by shop or user,
              and select a date range. The chart and table update together.
            </p>
          </div>
        ) : (
          <>
            {/* Loading state */}
            {loading && (
              <div className="bg-white shadow-sm rounded-xl p-12 text-center border mb-6">
                <svg className="mx-auto h-8 w-8 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">Loading performance data…</p>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-rose-50 border border-rose-100 rounded-xl px-4 py-3 mb-6 text-sm text-rose-700">
                {error}
              </div>
            )}

            {result && !loading && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <KPICard title={`Total ${result.metricLabel}`} value={kpiValue(result.kpis.total)} sub={`${dateFrom} → ${dateTo}`} color="indigo" />
                  <KPICard title={`Top ${groupName}`} value={result.kpis.top.name || '—'} sub={result.kpis.top.name ? kpiValue(result.kpis.top.value) : 'no data in range'} color="emerald" />
                  <KPICard title={`${groupName}s Active`} value={result.kpis.group_count} sub={`with ${result.metricLabel.toLowerCase()} in range`} color="sky" />
                  <KPICard title={`Avg per ${groupName}`} value={kpiValue(result.kpis.avg)} sub={result.contextLabel ? `context: ${result.contextLabel}` : undefined} color="amber" />
                </div>

                {/* Stacked Area Chart */}
                <div className="bg-white shadow-sm rounded-xl p-6 border mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {result.metricLabel} over Time by {groupName}
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">
                    Bucketed {result.granularity} {result.money ? '(net: gross − refunds)' : '(count of records)'}
                  </p>
                  {result.names.length > 0 ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <AreaChart data={result.series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => formatBucket(v, result.granularity)}
                        />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => (result.money ? `$${(parseFloat(v) || 0).toLocaleString('en-US')}` : String(v))}
                        />
                        <Tooltip
                          contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
                          formatter={(v, name) => [kpiValue(v), name]}
                          labelFormatter={(l) => formatBucket(l, result.granularity)}
                        />
                        <Legend />
                        {result.names.map((name, i) => (
                          <Area
                            key={`area-${name}-${i}`}
                            type="monotone"
                            dataKey={name}
                            stackId="metric"
                            stroke={COLORS[i % COLORS.length]}
                            fill={COLORS[i % COLORS.length]}
                            fillOpacity={0.15}
                            strokeWidth={2}
                            dot={false}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px]">
                      <p className="text-gray-400 text-sm">No {result.metricLabel.toLowerCase()} in this range.</p>
                    </div>
                  )}
                </div>

                {/* Ranked Table */}
                <div className="bg-white shadow-sm rounded-xl border overflow-hidden">
                  <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{result.metricLabel} by {groupName}</h3>
                      <p className="text-xs text-gray-400">
                        Total over {dateFrom} → {dateTo}, sorted descending
                      </p>
                    </div>
                    <button
                      onClick={exportCSV}
                      className="text-sm px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
                    >
                      Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{groupName}</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{result.metricLabel}</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% of Total</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{result.contextLabel}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.rows.map((r, i) => (
                          <tr key={r.name + i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                            <td className={`px-4 py-3 text-sm text-right font-semibold whitespace-nowrap ${result.money && r.value < 0 ? 'text-rose-600' : 'text-gray-900'}`}>
                              {kpiValue(r.value)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-500">{pctOfTotal(r.value)}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-600 whitespace-nowrap">{contextValue(r.context)}</td>
                          </tr>
                        ))}
                        {result.rows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                              No {result.metricLabel.toLowerCase()} found for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
